import {
  EventTypes,
  HISTORY_ACTION_MODE,
  nodesFlux,
  useEditor,
  useEditorSelector,
} from '@candulabs/craft';
import { SlateElement } from '@candulabs/craft/dist/craftSlate/types';
import { Callout, css, styled } from 'canoe';
import throttle from 'lodash/throttle';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Editor, Node, Transforms } from 'slate';
import { Editable, ReactEditor, Slate } from 'slate-react';

import { useOutsideClick } from 'commons/hooks';
import { NO_ACTION } from 'craft/constants';
import { useNodeContext } from 'craft/hooks';
import createRteEditor from 'craft/rte/utils/createEditor';
import { useViewportDom } from 'craft/state/viewport/selectors';
import { SlateNode } from 'craft/types';
import { findAncestor } from 'craft/utils';

import { Element, Leaf, Toolbar } from './components';
import {
  craftNodeToSlateNode,
  createTreeFromCraftNodes,
  slateNodesToCraft,
  slateToCraftNodes,
  Tree,
} from './formats';
import { useKeydown } from './hooks';

const StyledEditable = styled(Editable)<{ isInCollection?: string }>`
  ${({ theme, isInCollection }) => css`
    outline: 2px solid
      ${isInCollection === 'true' ? theme.colors.MangoOrangeD10 : theme.colors.BrandPurpleL10} !important;
  `}
`;

interface RichTextEditorProps {
  isInCollection?: boolean;
  innerRef: React.RefObject<HTMLDivElement>;
  targetedNodeId: string;
  onCopy?: (trees: Tree[]) => void;
}

enum SlateAction {
  Sync,
  Change,
  Undo,
  Redo,
}

export const RichTextEditor = ({
  isInCollection,
  targetedNodeId,
  innerRef,
  onCopy,
}: RichTextEditorProps) => {
  const rteEditor = useMemo(createRteEditor, []);
  const existingSelection = rteEditor.selection;
  const { id: nodeId } = useNodeContext();
  const { query, actions } = useEditor();
  const custom = useEditorSelector(nodesFlux.selectors.getNodeCustom(nodeId));
  const [latestAction, setLatestAction] = useState<SlateAction>(SlateAction.Change);
  const { viewportWrapperEl } = useViewportDom();

  const renderElement = useCallback(
    (props) => <Element {...props} onUpdateFromCraft={() => setLatestAction(SlateAction.Sync)} />,
    [],
  );
  const renderLeaf = useCallback((props) => <Leaf {...props} />, []);

  useEffect(() => {
    // seach for the node matching the targetNodeId
    const [match] = Array.from(
      Editor.nodes<SlateNode>(rteEditor, {
        at: [],
        match: (n) => (n as SlateNode)?.id === targetedNodeId,
      }),
    );

    if (!match || !match[1] || !match[0] || match[0].data?.isNodeDisabled) {
      // do not place the caret as no path was found for it
    } else {
      // placing the caret at the end of the targeted Node
      Transforms.select(rteEditor, Editor.end(rteEditor, match[1]));
    }
  }, [rteEditor, targetedNodeId]);

  useEffect(() => {
    if ([SlateAction.Undo, SlateAction.Redo, SlateAction.Sync].includes(latestAction)) {
      rteEditor.children = craftNodeToSlateNode(query, nodeId).children;
      Editor.normalize(rteEditor, { force: true });
      if (custom?.range) {
        rteEditor.selection = custom.range;
      }
      setLatestAction(SlateAction.Change);
    }
  }, [latestAction, custom?.range]);

  useEffect(() => {
    if ([SlateAction.Change].includes(latestAction)) {
      if (existingSelection) {
        ReactEditor.focus(rteEditor);
        Transforms.select(rteEditor, Editor.unhangRange(rteEditor, existingSelection));
      }
    }
  }, [latestAction, existingSelection]);

  const saveChangesToCraft = (newDocument: SlateElement[]) => {
    // start saving the slate
    const slateNodeIds = Array.from(Node.descendants(rteEditor)).map(
      ([descendant]) => (descendant as SlateNode).id,
    );
    const currentSlateNodeIds = query.node(nodeId).descendants(true);

    // Get deleted slate node ids
    const deletedNodeIds = currentSlateNodeIds.filter((id) => slateNodeIds.includes(id) === false);

    actions.setState(
      (state) => {
        const slateState = newDocument;
        const childNodeIds = slateState.map((node) => node.id) as string[];

        slateNodesToCraft(state, slateState as SlateElement[], nodeId);

        // eslint-disable-next-line no-param-reassign
        state.nodes[nodeId].data.nodes = childNodeIds;
        // eslint-disable-next-line no-param-reassign
        state.nodes[nodeId].data.custom.range = rteEditor.selection;
        // Remove any deleted Slate nodes from the Craft state
        // eslint-disable-next-line no-param-reassign
        deletedNodeIds.forEach((deletedNodeId) => delete state.nodes[deletedNodeId]);
      },
      HISTORY_ACTION_MODE.THROTTLE,
      500,
    );
  };

  const onChangeThrottled = (newDocument: any) => {
    try {
      saveChangesToCraft(newDocument);
      setLatestAction(SlateAction.Change);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn(e);
    }
  };

  const onChange = useCallback((newDoc: any) => throttle(() => onChangeThrottled(newDoc), 0)(), []);

  const { onKeyDown } = useKeydown({
    editor: rteEditor,
    callbacks: {
      onRedo: () => {
        window.requestAnimationFrame(() => {
          rteEditor.selection = null;
          setLatestAction(SlateAction.Redo);
        });
      },
      onUndo: () => {
        window.requestAnimationFrame(() => {
          rteEditor.selection = null;
          setLatestAction(SlateAction.Undo);
        });
      },
      onCopy: () => {
        if (!rteEditor.selection) {
          return;
        }
        const match = Editor.fragment(rteEditor, rteEditor.selection);
        const portableCraftNodes = slateToCraftNodes(query, match as SlateElement[], nodeId);

        const trees = createTreeFromCraftNodes(portableCraftNodes, nodeId);
        onCopy?.(trees);
      },
    },
  });

  const blurHandler = useCallback(
    (e: MouseEvent) => {
      const range = rteEditor.selection
        ? Editor.string(rteEditor, rteEditor.selection, { voids: true })
        : '';

      const hasNoActionAncestor = findAncestor(
        e.target as Element | null,
        `[data-focus-listener="${NO_ACTION}"],[candu-portal]`,
      );

      // prevent clicking on the toolbar to close the editor
      // prevent outside click while selecting a text to close the editor
      if (hasNoActionAncestor || !!range) {
        e.stopPropagation();
      } else {
        actions.setNodeEvent(EventTypes.focused, []);
      }
    },
    [actions, rteEditor],
  );

  useOutsideClick(innerRef.current, blurHandler);

  return (
    <>
      <Slate
        editor={rteEditor}
        value={craftNodeToSlateNode(query, nodeId).children}
        onChange={onChange}
      >
        <StyledEditable
          style={custom?.css}
          isInCollection={(isInCollection || false).toString()}
          autoFocus
          data-nid={nodeId}
          onKeyDown={onKeyDown}
          renderLeaf={renderLeaf}
          renderElement={renderElement}
          spellCheck={false}
        />
        <Callout
          referenceElement={innerRef.current}
          closeKey="Escape"
          placement="top-start"
          modifiers={[
            {
              name: 'preventOverflow',
              options: {
                boundary: viewportWrapperEl,
                padding: 8,
              },
            },
          ]}
        >
          {(calloutProps) => (
            <div
              data-craft-ignore-events
              data-focus-listener={NO_ACTION}
              ref={calloutProps.ref}
              style={{ ...calloutProps.style, zIndex: '100' }}
              {...calloutProps.attributes.popper}
            >
              <Toolbar />
            </div>
          )}
        </Callout>
      </Slate>
    </>
  );
};
